import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { useAppDispatch, useAppSelector } from 'src/state/store/hooks';
import TextEditor, {
  getAllowedFileTypes,
} from '../../../components/Correspondence/TextEditor';
import { useAddCorrespondenceMutation } from 'src/state/slice/correspondenceApi';
import { CaseDetailsContext } from '../providers/CaseDetailsProvider';
import {
  addCorrespondenceSlice,
  addTextContent,
  setDisplayOnPublicPortal,
  reset as resetAddCorrespondenceState,
} from 'src/state/slice/addCorrespondenceSlice';

import { debounce } from 'lodash';
import { KatAlert, KatFileUpload } from '@amzn/katal-react';
import { convertFileToDocumentDetails as convert } from 'src/state/slice/common/FileUploadReducers';
import {
  CorrespondenceTextEditorConstants,
  UploaderConstants,
} from 'src/constants/ProjectConstants';
import { IDmsFile } from 'src/integration/dms/DmsModel';
import DmsFileUploadClient from 'src/integration/dms/DmsFileUploadClient';
import {
  FILE_EXTENSION,
  UI_CLIENTS,
  UPLOAD_MAX_FILE_SIZE,
} from 'src/constants/GlobalConstants';
import { logger } from 'src/logger';
import { emitClickMetrics, MetricsName } from 'src/metrics';
import {
  AddCorrespondenceTypeRequest,
  CorrespondenceType,
} from 'src/state/slice/models/CorrespondenceTypes';
import {
  FileAttributes,
  convertFileToFileAttributesAction as toFileAttributes,
} from 'src/state/slice/common/FileUploadStatusReducers';
import { isValidFileType } from 'src/components/FileUpload/Uploader';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { PageContext } from 'src/state/providers/PageProvider';
import {
  CONFIG_TYPE,
  MAX_NUMBER_OF_ATTACHMENTS,
  MAX_TOTAL_ATTACHMENTS_SIZE,
} from 'src/constants/GlobalConstants';
import { fetchFromAppConfig } from 'src/config-store/ConfigFetcher';
import { validateFileAttachments } from 'src/utilities/AttachmentValidationUtils';

const TextEditorContainer = () => {
  /**
   * Redux State
   */
  const dispatch = useAppDispatch();
  const {
    textContent,
    attachments,
    fileUploadStatus,
    displayOnPublicPortal,
    attachmentValidationError,
  } = useAppSelector((state) => state.addCorrespondence);

  // Local state to track all files user has attempted to attach (for validation purposes)
  const [attemptedFiles, setAttemptedFiles] = useState<File[]>([]);
  const {
    addAttachment,
    removeAttachment,
    replaceAttachment,
    addFileToUploadTracker,
    setFileToUploadComplete,
    removeFileFromUploadTracker,
    setFileToError,
    setAttachmentValidationError,
    clearAttachmentValidationError,
  } = addCorrespondenceSlice.actions;

  /**
   * Cleanup during unmounting
   */
  useEffect(() => {
    return () => {
      dispatch(resetAddCorrespondenceState());
    };
  }, []);

  /**
   * Contexts
   */
  const { caseId } = useContext(CaseDetailsContext);
  const authDetails = useContext(AuthContext);
  const { pageScope } = useContext(PageContext);

  /**
   * API Definition & States
   */
  const [addCorrespondence, { data, error }] = useAddCorrespondenceMutation();

  const { fileUploaderBundle } = useContext(TranslationContext);
  const errorSummary: string = fileUploaderBundle.getMessage(
    UploaderConstants.ERROR_GENERIC_MESSAGE_FOR_UI,
  );
  const allowedFileTypes: FILE_EXTENSION[] | undefined = getAllowedFileTypes(
    authDetails.preferredAccountId,
    authDetails.clientId,
  );

  const totalAttachmentsSize = fetchFromAppConfig(
    authDetails.preferredAccountId,
    authDetails.clientId,
    CONFIG_TYPE.PROPERTIES,
    MAX_TOTAL_ATTACHMENTS_SIZE,
  );

  const maxNumberOfAttachments = fetchFromAppConfig(
    authDetails.preferredAccountId,
    authDetails.clientId,
    CONFIG_TYPE.PROPERTIES,
    MAX_NUMBER_OF_ATTACHMENTS,
  );

  useEffect(() => {
    if (data) {
      logger.info(
        `Successfully posted correspondence for user ${authDetails.customerId}. Response is:`,
        data,
      );
      //During the page load component will remount,
      //this will trigger the resetAddCorrespondenceState
      if (attachments && attachments.length > 0) {
        location.reload();
      } else {
        dispatch(resetAddCorrespondenceState());
      }
    }
  }, [data]);

  /**
   * Methods
   */
  const onEditorSubmit = () => {
    const addCorrespondenceRequest: AddCorrespondenceTypeRequest = {
      userId: authDetails.customerId,
      userEmail: authDetails.emailId,
      caseId: caseId,
      correspondence: textContent,
      documentList: attachments,
      accountId: authDetails.preferredAccountId,
      clientId: authDetails.clientId,
      correspondenceType:
        displayOnPublicPortal ||
        UI_CLIENTS.RegulatorCentral == authDetails?.clientId
          ? CorrespondenceType.POST
          : CorrespondenceType.POST_INTERNAL_ONLY,
    };
    logger.info(`Adding correspondence for CaseId: ${caseId}. Request is:`, {
      addCorrespondenceRequest,
    });
    addCorrespondence(addCorrespondenceRequest);

    dispatch(addTextContent(''));
  };

  const updateTextContent = (text: string) => {
    dispatch(addTextContent(text));
    logger.debug(
      `Adding partial correspondence text to state for caseId ${caseId}. Text is ${text}`,
    );
  };

  const onFilesAttached = (e: KatFileUpload.FilesAttachedEvent) => {
    // Clear any existing validation error
    dispatch(clearAttachmentValidationError());

    const newFiles = Array.from(e.detail.files);

    // Add new files to attempted files list for validation
    const updatedAttemptedFiles = [...attemptedFiles, ...newFiles];
    setAttemptedFiles(updatedAttemptedFiles);

    const validationResult = validateFileAttachments(
      updatedAttemptedFiles,
      maxNumberOfAttachments,
      totalAttachmentsSize,
    );

    if (validationResult.error) {
      const errorMessage = fileUploaderBundle.formatMessage(
        validationResult.error.messageId,
        validationResult.error.messageParams,
      );
      logger.error(
        `File attachment validation failed for user ${authDetails.customerId}: ${errorMessage}`,
      );
      dispatch(setAttachmentValidationError(errorMessage));
    }

    e.detail.files.forEach((file) => {
      const fileAttribute: FileAttributes = toFileAttributes(
        file,
        errorSummary,
      );
      if (file.size >= UPLOAD_MAX_FILE_SIZE) {
        dispatch(setFileToError(fileAttribute));
        logger.error(
          `Size exceeded while uploading: ${file.name} by user ${authDetails.customerId}. File Size ${file.size}bytes`,
        );
        return;
      }
      const fileExtension: string | undefined = file.name.includes('.')
        ? file.name.split('.').pop()
        : undefined;
      if (!isValidFileType(fileExtension, allowedFileTypes)) {
        logger.error(
          `${authDetails.customerId} uploading an invalid file type: ${file.name}`,
        );
        dispatch(setFileToError(fileAttribute));
        return;
      }
      dispatch(addFileToUploadTracker(fileAttribute));
      const fileUploadClient = new DmsFileUploadClient(file, pageScope);
      fileUploadClient
        .orchestrateFileUpload()
        .then((dmsFile: IDmsFile) => {
          dispatch(setFileToUploadComplete(fileAttribute));
          logger.info(
            `Uploaded file: ${file.name} by user: ${authDetails.customerId}. DMS Id: ${dmsFile.documentId}`,
          );
          return dmsFile;
        })
        .then((dmsFile: IDmsFile) => {
          emitClickMetrics(MetricsName.CORRESPONDENCE_TAB, 'DocumentUpload');
          dispatch(addAttachment(convert(dmsFile.documentId, file)));
        })
        .catch((error: Error) => {
          logger.error(
            `Error occured while uploading file: ${file.name} by: ${
              authDetails.customerId
            }. Check reason in context. 
             ${JSON.stringify(error)}`,
          );
          dispatch(setFileToError(fileAttribute));
        });
    });
  };

  const onFilesRemoved = (e: KatFileUpload.FilesRemovedEvent) => {
    const removedFiles = Array.from(e.detail.files);

    // Remove files from local attempted files state
    const updatedAttemptedFiles = attemptedFiles.filter(
      (attemptedFile) =>
        !removedFiles.some(
          (removedFile) => removedFile.name === attemptedFile.name,
        ),
    );
    setAttemptedFiles(updatedAttemptedFiles);

    removedFiles.forEach((file) => {
      //Id is not being compared in removing the file. Name is used instead. Hence we can use the name as the Id.
      dispatch(removeAttachment(convert(file.name, file)));
      dispatch(removeFileFromUploadTracker(toFileAttributes(file)));
      emitClickMetrics(MetricsName.CORRESPONDENCE_TAB, 'DocumentDeleted');
      logger.info(
        `Removed file: ${file.name} by user: ${authDetails.customerId}.`,
      );
    });

    // Re-validate the current state using updated local state
    const validationResult = validateFileAttachments(
      updatedAttemptedFiles, // Updated attempted files after removal
      maxNumberOfAttachments,
      totalAttachmentsSize,
    );

    // Update validation error based on current state
    if (validationResult.isValid) {
      dispatch(clearAttachmentValidationError());
      logger.info(
        `Cleared validation error after file removal by user: ${authDetails.customerId}`,
      );
    } else if (validationResult.error) {
      // Update validation error to reflect current state after removal
      const errorMessage = fileUploaderBundle.formatMessage(
        validationResult.error.messageId,
        validationResult.error.messageParams,
      );
      dispatch(setAttachmentValidationError(errorMessage));
      logger.info(
        `Updated validation error after file removal by user: ${authDetails.customerId}. New error: ${errorMessage}`,
      );
    }
  };

  const onFilesReplaced = (e: KatFileUpload.FilesReplacedEvent) => {
    // Clear any existing validation error
    dispatch(clearAttachmentValidationError());

    const newFiles = Array.from(e.detail.newFiles);
    const oldFiles = Array.from(e.detail.oldFiles);

    // Update local attempted files state - replace old files with new files
    const oldFileNames = oldFiles.map((file) => file.name);
    const updatedAttemptedFiles = [
      ...attemptedFiles.filter((file) => !oldFileNames.includes(file.name)),
      ...newFiles,
    ];
    setAttemptedFiles(updatedAttemptedFiles);

    const validationResult = validateFileAttachments(
      updatedAttemptedFiles,
      maxNumberOfAttachments,
      totalAttachmentsSize,
    );

    if (validationResult.error) {
      const errorMessage = fileUploaderBundle.formatMessage(
        validationResult.error.messageId,
        validationResult.error.messageParams,
      );
      logger.error(
        `File replacement validation failed for user ${authDetails.customerId}: ${errorMessage}`,
      );
      dispatch(setAttachmentValidationError(errorMessage));
    }

    e.detail.newFiles.forEach((newFile, index) => {
      const oldFile = e.detail.oldFiles[index];
      const oldFileAttribute: FileAttributes = toFileAttributes(
        oldFile,
        errorSummary,
      );
      const newFileAttribute: FileAttributes = toFileAttributes(newFile);
      if (newFile.size >= UPLOAD_MAX_FILE_SIZE) {
        dispatch(setFileToError(oldFileAttribute));
        logger.error(
          `Size exceeded while uploading: ${newFile.name} by user ${authDetails.customerId}. File Size ${newFile.size}bytes`,
        );
        return;
      }
      dispatch(addFileToUploadTracker(newFileAttribute));
      const fileUploadClient = new DmsFileUploadClient(newFile, pageScope);
      fileUploadClient
        .orchestrateFileUpload()
        .then((dmsNewFile: IDmsFile) => {
          dispatch(setFileToUploadComplete(newFileAttribute));
          logger.info(
            `Uploaded file: ${newFile.name} by user: ${authDetails.customerId}. DMS Id: ${dmsNewFile.documentId}`,
          );
          return dmsNewFile;
        })
        .then((dmsNewFile: IDmsFile) => {
          dispatch(
            replaceAttachment({
              newFile: convert(dmsNewFile.documentId, newFile),
              //Id is not being compared in removing the file. Name is used instead. Hence we can use the name as the Id.
              oldFile: convert(oldFile.name, oldFile),
            }),
          );
          logger.info(
            `File: ${newFile.name} saved successfully by user ${authDetails.customerId} to state`,
          );
        })
        .catch((error: Error) => {
          logger.error(
            `Error occured while uploading file: ${newFile.name} by: ${authDetails.customerId}. Check reason in context`,
            error,
          );
          dispatch(setFileToError(oldFileAttribute));
        });
    });
  };

  if (error) {
    logger.error(
      `Error Occured while saving correspondence for CaseId: ${caseId}. Details  ${JSON.stringify(
        error,
      )}`,
    );
  }

  const { messageBundle } = useContext(TranslationContext);

  return (
    <>
      {error && (
        <KatAlert
          description={messageBundle.getMessage(
            CorrespondenceTextEditorConstants.ERROR_POST_CORRESPONDENCE,
          )}
          variant="danger"
        />
      )}
      <TextEditor
        editorContent={textContent}
        updateEditorContent={debounce(updateTextContent, 500)}
        onEditorSubmit={onEditorSubmit}
        onFilesAttached={onFilesAttached}
        onFilesRemoved={onFilesRemoved}
        onFilesReplaced={onFilesReplaced}
        fileStates={fileUploadStatus.fileUploadTracker}
        disableSubmitButton={
          fileUploadStatus.uploadErrorCount > 0 ||
          fileUploadStatus.currentUploadInProgress !== 0 ||
          !textContent ||
          !!attachmentValidationError
        }
        attachmentValidationError={attachmentValidationError || undefined}
        displayOnPublicPortal={displayOnPublicPortal}
        setDisplayOnPublicPortal={(data: boolean) => {
          dispatch(setDisplayOnPublicPortal(data));
        }}
      ></TextEditor>
    </>
  );
};

export default TextEditorContainer;
